import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

export function LoginPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  if (session) {
    const dest = (location.state as { redirect?: string } | null)?.redirect ?? '/';
    navigate(dest, { replace: true });
    return null;
  }

  async function sendMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/` },
    });
    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }
    setStatus('sent');
  }

  return (
    <div className="h-screen flex items-center justify-center bg-bg-base">
      <div className="w-full max-w-md p-8 bg-bg-panel border border-border rounded-2xl shadow-2xl">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-9 h-9 rounded-[9px] bg-gradient-to-br from-accent to-ai flex items-center justify-center">
            <Icon name="clapperboard" size={18} className="text-white" />
          </div>
          <span className="text-xl font-semibold">topStudio</span>
        </div>
        <h1 className="text-lg font-semibold mb-1">Logga in</h1>
        <p className="text-text-muted text-sm mb-6">
          Vi mailar dig en magisk lank. Inget losenord behovs.
        </p>

        {status === 'sent' ? (
          <div className="bg-[rgba(34,197,94,0.1)] border border-[rgba(34,197,94,0.3)] text-green-400 rounded-lg px-4 py-3 text-sm flex items-start gap-2">
            <Icon name="mail-check" size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">Lank skickad</div>
              <div className="text-text-muted mt-1">
                Kolla {email}. Klicka pa lanken for att logga in.
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={sendMagicLink} className="space-y-3">
            <label className="block">
              <span className="text-xs text-text-muted block mb-1.5">E-post</span>
              <input
                type="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="du@exempel.se"
                className="w-full bg-bg-elevated border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-accent"
              />
            </label>
            {errorMsg && (
              <div className="text-red-400 text-xs flex items-center gap-1.5">
                <Icon name="alert-circle" size={14} />
                {errorMsg}
              </div>
            )}
            <button
              type="submit"
              disabled={status === 'sending' || !email}
              className="w-full btn btn-primary justify-center py-2.5 disabled:opacity-50"
            >
              {status === 'sending' ? (
                <>
                  <Icon name="loader-2" size={14} className="animate-spin" />
                  Skickar...
                </>
              ) : (
                <>
                  <Icon name="send" size={14} />
                  Skicka magisk lank
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
